import { useEffect, useState } from "react";
import { fetchReviews, createReview } from "@/lib/api";
import { toast } from "sonner";
import { Star, Loader2, MessageSquareQuote } from "lucide-react";

const StarRow = ({ value = 0, onChange, size = "sm", testIdPrefix }) => {
  const isInteractive = typeof onChange === "function";
  const sizeCls = size === "lg" ? "w-6 h-6" : "w-4 h-4";
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((s) => {
        const active = s <= value;
        const Component = isInteractive ? "button" : "span";
        return (
          <Component
            key={s}
            type={isInteractive ? "button" : undefined}
            data-testid={testIdPrefix ? `${testIdPrefix}-${s}` : undefined}
            onClick={isInteractive ? () => onChange(s) : undefined}
            className={`${
              isInteractive ? "cursor-pointer" : ""
            } transition-colors`}
            aria-label={isInteractive ? `Rate ${s} star${s > 1 ? "s" : ""}` : undefined}
          >
            <Star
              className={`${sizeCls} ${
                active
                  ? "fill-[var(--bw-accent)] text-[var(--bw-accent)]"
                  : "text-[var(--bw-border)]"
              }`}
            />
          </Component>
        );
      })}
    </div>
  );
};

export const ReviewSection = ({ bookId }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    fetchReviews(bookId)
      .then(setReviews)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !comment.trim() || rating < 1) {
      toast.error("Please add your name, a rating, and a comment.");
      return;
    }
    setSubmitting(true);
    try {
      await createReview(bookId, {
        reviewer_name: name.trim(),
        rating,
        comment: comment.trim(),
      });
      toast.success("Thanks — your review is live.");
      setName("");
      setRating(0);
      setComment("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  const avg =
    reviews.length > 0
      ? reviews.reduce((a, r) => a + (r.rating || 0), 0) / reviews.length
      : 0;

  return (
    <section
      data-testid="reviews-section"
      className="mt-16 border-t border-[var(--bw-border)] pt-12"
    >
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="font-mono-plex text-xs uppercase tracking-[0.35em] text-[var(--bw-secondary)]">
            — reader reviews
          </p>
          <h2 className="font-serif-display text-4xl md:text-5xl leading-none mt-2">
            What readers say
          </h2>
        </div>
        {reviews.length > 0 && (
          <div className="flex items-center gap-3">
            <StarRow value={Math.round(avg)} />
            <span
              data-testid="reviews-avg"
              className="font-mono-plex text-sm text-[var(--bw-secondary)]"
            >
              {avg.toFixed(1)} · {reviews.length} review
              {reviews.length > 1 ? "s" : ""}
            </span>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-12 gap-10">
        {/* Form */}
        <form
          data-testid="review-form"
          onSubmit={submit}
          className="lg:col-span-5 bg-white border border-[var(--bw-primary)]/15 p-6 md:p-8 h-fit"
        >
          <h3 className="font-serif-display text-2xl mb-6">
            Leave your review
          </h3>
          <div>
            <label className="font-mono-plex text-[10px] uppercase tracking-widest text-[var(--bw-secondary)]">
              Your name
            </label>
            <input
              data-testid="review-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full border border-[var(--bw-primary)]/20 bg-white p-3 outline-none focus:border-[var(--bw-accent)] text-sm"
            />
          </div>
          <div className="mt-4">
            <label className="font-mono-plex text-[10px] uppercase tracking-widest text-[var(--bw-secondary)] block mb-2">
              Your rating
            </label>
            <StarRow
              value={rating}
              onChange={setRating}
              size="lg"
              testIdPrefix="review-star"
            />
          </div>
          <div className="mt-4">
            <label className="font-mono-plex text-[10px] uppercase tracking-widest text-[var(--bw-secondary)]">
              Comment
            </label>
            <textarea
              data-testid="review-comment-input"
              rows={4}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="mt-1 w-full border border-[var(--bw-primary)]/20 bg-white p-3 outline-none focus:border-[var(--bw-accent)] text-sm"
            />
          </div>
          <button
            data-testid="review-submit-button"
            disabled={submitting}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[var(--bw-primary)] text-[var(--bw-bg)] py-3.5 hover:bg-[var(--bw-accent)] transition-colors disabled:opacity-70"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Posting...
              </>
            ) : (
              "Post review"
            )}
          </button>
        </form>

        {/* List */}
        <div className="lg:col-span-7 space-y-6">
          {loading ? (
            <div className="text-sm text-[var(--bw-secondary)]">
              Loading reviews…
            </div>
          ) : reviews.length === 0 ? (
            <div
              data-testid="reviews-empty"
              className="border border-dashed border-[var(--bw-border)] p-10 text-center"
            >
              <MessageSquareQuote className="w-6 h-6 text-[var(--bw-accent)] mx-auto" />
              <p className="font-serif-display text-2xl mt-3">
                Be the first to review this summary.
              </p>
              <p className="text-sm text-[var(--bw-secondary)] mt-1">
                Tell other readers what you took away in five pages.
              </p>
            </div>
          ) : (
            reviews.map((r) => (
              <article
                key={r.id}
                data-testid={`review-item-${r.id}`}
                className="border border-[var(--bw-border)] bg-[var(--bw-surface)] p-6"
              >
                <header className="flex items-center justify-between gap-4">
                  <div>
                    <p className="font-serif-display text-xl leading-none">
                      {r.reviewer_name}
                    </p>
                    <p className="font-mono-plex text-[10px] uppercase tracking-widest text-[var(--bw-secondary)] mt-1">
                      {new Date(r.created_at).toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </p>
                  </div>
                  <StarRow value={r.rating} />
                </header>
                <p className="mt-4 text-sm md:text-base leading-relaxed whitespace-pre-line">
                  {r.comment}
                </p>
              </article>
            ))
          )}
        </div>
      </div>
    </section>
  );
};

export default ReviewSection;
